import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, Button, IconButton, Modal, Typography } from "@mui/material";
import { getFileExtension, getOfficeUrl } from "../../../utils/helpers";

const styles = {
  container: {
    position: "relative",
    zIndex: 1000,
    mt: 2,
    textAlign: "center",
  },
  button: {
    backgroundColor: "#1976d2",
    color: "white",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
    px: 2,
    py: 1,
    minWidth: "100px",
  },
  modalContainer: {
    bgcolor: "background.paper",
    p: 1,
    borderRadius: 2,
    width: {
      xs: "90%",
      sm: "80%",
      md: "95%",
    },
    height: "95vh",
    maxHeight: "100vh",
    boxShadow: 24,
    position: "relative",
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  title: {
    mb: 2,
    fontWeight: 600,
  },
};

const DocFilePreview = ({ selectedFile, isModalOpen, setIsModalOpen }) => {
  if (!selectedFile) return null;

  const ext = getFileExtension(selectedFile.documentName);
  const url = selectedFile.viewUrl;
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", selectedFile.documentName || "file");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isUnsupported = !(
    ext === "pdf" ||
    ["docx", "odt", "pptx", "xlsx", "word"].includes(ext) ||
    ["jpg", "jpeg", "png"].includes(ext)
  );  

  const renderFile = () => {
    if (ext === "pdf") {
      return (
        <Box height="90vh">
          <iframe
            src={url}
            width="100%"
            height="100%"
            title="PDF Preview"
            style={{ border: "none" }}
          />
        </Box>
      );
    }

    if (["docx", "odt", "pptx", "xlsx", "word"].includes(ext)) {
      const officeUrl = getOfficeUrl(url);
      return (
        <Box height="90vh">
          <iframe
            src={officeUrl}
            width="100%"
            height="100%"
            title="Document Preview"
            style={{ border: "none" }}
          />
        </Box>
      );
    }

    if (["jpg", "jpeg", "png"].includes(ext)) {
      return (
        <Box display="flex" justifyContent="center">
          <img
            src={url}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
            }}
          />
        </Box>
      );
    }

    // Unsupported file
    return (
      <Box textAlign="center" mt={10}>
        <Typography variant="body1" mb={2}>
          Preview not supported for this file type: {ext}
        </Typography>
        <div style={styles.container}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={styles.button}
          >
            Download File
          </Button>
        </div>
      </Box>
    );
  };

  const onClose = () => {
    setIsModalOpen(false);
  };

  return (
    <Modal open={isModalOpen}>
       <Box
        sx={{
          ...styles.modalContainer,
          m: isUnsupported ? "10% auto" : "1% auto",
          height: isUnsupported ? "40vh" : "95vh",
          width: isUnsupported ? "70vh" : "95%",
          maxHeight: isUnsupported ? "50vh" : "100vh",
        }}
      >
        {/* Close Button */}
        <IconButton onClick={onClose} sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>

        {/* File Title */}
        <Typography variant="h6" sx={styles.title}>
          {selectedFile.documentName}
        </Typography>

        {/* File Renderer */}
        {renderFile()}
      </Box>
    </Modal>
  );
};

export default DocFilePreview;
